import type { BuilderWeights, WeightSet } from './types';
import { BUILDER_WEIGHTS, DEFAULT_WEIGHTS } from './types';

/**
 * Map an organisation's `WeightSet` (signal classes) onto the day scorer's
 * component weights.
 *
 * The scorer only models two classes today:
 * - `fit`      → keyword + city (+ hotel proximity in `rankDayCatalog`)
 * - `evidence` → popularity + co-occurrence
 *
 * Each class scales its components by `weight / defaultWeight`, so
 * `DEFAULT_WEIGHTS` maps to `BUILDER_WEIGHTS` exactly and `NEUTRAL_WEIGHTS`
 * (nothing weighted) mutes every signal, leaving only the deterministic
 * tie-break. A zero mutes a class — it can never change membership (R3), only
 * order. `curation` / `commercial` / `quality` have no signal in this scorer
 * yet and are ignored rather than guessed at.
 */
const classScale = (
  weight: number | undefined,
  defaultWeight: number | undefined
): number => {
  const base = defaultWeight ?? 0;
  if (base <= 0) return 0;
  const value = weight ?? 0;
  return Number.isFinite(value) && value > 0 ? value / base : 0;
};

export const builderWeightsFor = (set: WeightSet): BuilderWeights => {
  const evidence = classScale(
    set.weights.evidence,
    DEFAULT_WEIGHTS.weights.evidence
  );
  const fit = classScale(set.weights.fit, DEFAULT_WEIGHTS.weights.fit);
  return {
    popularity: BUILDER_WEIGHTS.popularity * evidence,
    coOccurrence: BUILDER_WEIGHTS.coOccurrence * evidence,
    keyword: BUILDER_WEIGHTS.keyword * fit,
    city: BUILDER_WEIGHTS.city * fit,
  };
};

/**
 * How much of the default `fit` weight the given component weights carry.
 * Used to scale the additive hotel-proximity boost with the same knob.
 */
export const fitScaleOf = (weights: BuilderWeights | undefined): number =>
  weights
    ? (weights.keyword + weights.city) /
      (BUILDER_WEIGHTS.keyword + BUILDER_WEIGHTS.city)
    : 1;
